/**
 * Authenticated API hook.
 * @file src/web/shared/hooks/useAppApi.ts
 *
 * @llm-rule WHEN: Any feature page needs to call /api/*
 * @llm-rule AVOID: Calling useApi() directly — you'll lose the bearer token and frontend key
 * @llm-rule NOTE: Wraps uikit's useApi with the auth header read at hook time, so a
 *   token refreshed elsewhere is picked up on the next render without a reload.
 */

import { useCallback, useMemo, useState } from 'react';
import { useApi } from '@bloomneo/uikit';
import { config } from '../../features/auth/config';

/** Read the token fresh rather than closing over it — logout must take effect immediately. */
function currentToken(): string | null {
  return localStorage.getItem(config.auth.storage.token);
}

export function useAppApi() {
  const token = currentToken();

  return useApi({
    baseURL: config.api.baseUrl,
    headers: {
      [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

/**
 * Small request helper for one-off calls where the full useApi state machine
 * is overkill (a button that POSTs and refetches). Returns `null` on failure
 * after surfacing the message, so callers can `if (!result) return`.
 */
export function useAppRequest() {
  const [pending, setPending] = useState(false);

  const request = useCallback(
    async <T,>(
      path: string,
      init: { method?: string; body?: unknown } = {},
    ): Promise<{ ok: true; data: T } | { ok: false; error: string }> => {
      setPending(true);
      try {
        const token = currentToken();
        const response = await fetch(`${config.api.baseUrl}${path}`, {
          method: init.method ?? 'GET',
          headers: {
            'Content-Type': 'application/json',
            [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          // AppKit error middleware returns { error, message }. Prefer the
          // human-readable half, fall back to the code, then the status.
          const message =
            payload?.message || payload?.error || `Request failed (${response.status})`;
          return { ok: false, error: String(message) };
        }

        return { ok: true, data: payload as T };
      } catch (err: any) {
        return { ok: false, error: err?.message || 'Network error' };
      } finally {
        setPending(false);
      }
    },
    [],
  );

  return useMemo(() => ({ request, pending }), [request, pending]);
}
